<template>
	<div>
		<UProgress
			v-if="loading"
			color="secondary"
			class="max-w-sm w-full mx-auto"
		/>
		<div v-else>
			<div
				v-if="loggedIn"
				class="flex pt-14"
			>
				<UButton
					:id="LOGIN_VIEW_ACCOUNT_BUTTON_ID"
					:to="`${accountLinkPage || accountUrl}`"
					:label="accountLinkText"
					:aria-label="$t('login.view-account')"
					class="px-6 py-4 m-auto"
					size="lg"
					@click="trackEvent($event, trackingLocation)"
					@mouseenter="trackEvent($event, trackingLocation)"
				/>
			</div>
			<div v-else>
				<UForm
					:id="LOGIN_FORM_ID"
					:schema="schema"
					:state="state"
					class="flex flex-col w-full space-y-4 items-center"
					@submit="onSubmit"
				>
					<div class="flex flex-col sm:flex-row space-y-8 sm:space-x-4 justify-between w-full">
						<UFormField
							:label="$t('login.username')"
							name="email"
							class="w-full"
						>
							<UInput
								:id="LOGIN_FORM_FIELD_IDS.email"
								v-model="state.email"
								type="email"
								class="w-full"
								:aria-label="$t('login.username')"
								:autocomplete="EMAIL_AUTOCOMPLETE"
								@focus="trackFormEvent($event, trackingLocation)"
								@blur="trackFormEvent($event, trackingLocation)"
								@input="trackFormEvent($event, trackingLocation)"
								@mouseenter="trackFormEvent($event, trackingLocation)"
							/>
						</UFormField>

						<UFormField
							:label="$t('login.password')"
							name="password"
							class="w-full"
						>
							<UInput
								:id="LOGIN_FORM_FIELD_IDS.password"
								v-model="state.password"
								:type="showPassword ? 'text' : 'password'"
								:ui="{ trailing: 'pe-1' }"
								class="w-full"
								:aria-label="$t('login.password')"
								:autocomplete="PASSWORD_AUTOCOMPLETE"
								@focus="trackFormEvent($event, trackingLocation)"
								@blur="trackFormEvent($event, trackingLocation)"
								@input="trackFormEvent($event, trackingLocation)"
								@mouseenter="trackFormEvent($event, trackingLocation)"
							>
								<template #trailing>
									<UButton
										color="neutral"
										variant="link"
										size="sm"
										:icon="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
										:aria-label="showPassword ? 'Hide password' : 'ShowPassword password'"
										:aria-pressed="showPassword"
										aria-controls="password"
										@click="showPassword = !showPassword"
									/>
								</template>
							</UInput>
						</UFormField>
					</div>
					<UButton
						:id="LOGIN_FORM_FIELD_IDS.submit"
						:label="submitButtonText"
						loading-auto
						type="submit"
						size="xl"
						color="primary"
						class="w-full max-w-sm flex justify-center uppercase mt-8 sm:mt-0"
						@click="trackFormEvent($event, trackingLocation)"
						@mouseenter="trackFormEvent($event, trackingLocation)"
						@focus="trackFormEvent($event, trackingLocation)"
						@blur="trackFormEvent($event, trackingLocation)"
					/>
				</UForm>
				<div class="w-full text-center mt-6">
					<UButton
						:id="LOGIN_FORGOT_PASSWORD_BUTTON_ID"
						:to="localePath('/forgot-password')"
						class="link"
						color="primary"
						variant="ghost"
						@click="trackEvent($event, trackingLocation)"
						@mouseenter="trackEvent($event, trackingLocation)"
						@focus="trackEvent($event, trackingLocation)"
					>
						{{ $t('login.forgot') }}
					</UButton>
				</div>
			</div>
		</div>
		<ModalTwoFactorAuthentication
			v-model="modalOpen"
			@confirm="handle2FAVerification"
			@cancel="handle2FACancel"
			@close="handle2FAClose"
		/>
	</div>
</template>

<script setup lang="ts">
import { useAuth } from '@/composables/useAuth'
import { useEngagementTimer } from '@/composables/useEngagementTimer'
import { useGA4Tracking } from '@/composables/useGA4Tracking'
import { useLogin } from '@/composables/useLogin'
import { useZod } from '@/composables/useZod'
import {
	EMAIL_AUTOCOMPLETE, PASSWORD_AUTOCOMPLETE
} from '@/constants/form/autocompletes'
import {
	LOGIN_FORGOT_PASSWORD_BUTTON_ID,
	LOGIN_FORM_FIELD_IDS,
	LOGIN_FORM_ID,
	LOGIN_FORM_TYPE,
	LOGIN_TWO_FACTOR_AUTH_BUTTON_ID,
	LOGIN_TWO_FACTOR_AUTH_ENGAGEMENT_ACTION_ID,
	LOGIN_VIEW_ACCOUNT_BUTTON_ID
} from '@/constants/ga4/tracking-ids/forms/login'
import type { FormSubmitEvent } from '@nuxt/ui'
import { useI18n } from 'vue-i18n'
import * as z from 'zod'

const localePath = useLocalePath()

const {
	trackEvent,
	trackEngagement,
	trackFormEvent,
	trackFormSubmit
} = useGA4Tracking()

const {
	startTimer,
	stopTimer
} = useEngagementTimer()

const { zEmail } = useZod()

const modalOpen = ref(false)
const { login } = useLogin(modalOpen)

// engagement tracking timer key
const TWO_FA_TIMER_KEY = 'two-factor-auth'

const {
	loggedIn, accountUrl, loading
} = useAuth()
const { t } = useI18n({
	useScope: 'global'
})

const props = defineProps({
	customSubmitText: {
		type: String,
		default: ''
	},
	customAccountLinkText: {
		type: String,
		default: ''
	},
	accountLinkPage: {
		type: String,
		default: ''
	},
	trackingLocation: {
		type: String,
		required: true
	}
})

const {
	customSubmitText,
	customAccountLinkText,
	accountLinkPage,
	trackingLocation
} = toRefs(props)

const schema = z.object({
	email: zEmail,
	password: z.string()
})

type Schema = z.output<typeof schema>

const state = reactive<Partial<Schema>>({
	email: '',
	password: ''
})

const showPassword = ref(false)

const submitButtonText = computed(() => {
	return customSubmitText.value || t('login.button-text')
})

const accountLinkText = computed(() => {
	return customAccountLinkText.value || t('login.view-account')
})

const onSubmit = async (event: FormSubmitEvent<Schema>) => {
	// Track form submission attempt
	trackFormSubmit(LOGIN_FORM_ID, LOGIN_FORM_TYPE, trackingLocation.value, {
		has_email: !!event.data.email,
		has_password: !!event.data.password
	})

	await login(event.data.email, event.data.password)
}

watch(modalOpen, newValue => {
	if (newValue) {
		// Start engagement timer when modal opens
		startTimer(TWO_FA_TIMER_KEY)
	}
}, { immediate: true })

const handle2FAVerification = async (otp: number) => {
	let attempt = 'fail'
	try {
		const result = await login(state.email as string, state.password as string, otp)
		// If login returns a truthy value or success indicator, mark as success
		if (result && result.success !== false) {
			attempt = 'success'
		}
	} catch {
		attempt = 'fail'
	}

	const engagementMs = stopTimer(TWO_FA_TIMER_KEY)

	// Track 2FA verification attempt
	trackEngagement(LOGIN_TWO_FACTOR_AUTH_ENGAGEMENT_ACTION_ID, LOGIN_TWO_FACTOR_AUTH_BUTTON_ID, engagementMs, {
		form_id: LOGIN_FORM_ID,
		location: trackingLocation.value,
		attempt
	})

	modalOpen.value = false
}

const handle2FACancel = () => { // REVIEW: This may not be needed if close event is triggered on cancel click
	const engagementMs = stopTimer(TWO_FA_TIMER_KEY)

	trackEngagement(LOGIN_TWO_FACTOR_AUTH_ENGAGEMENT_ACTION_ID, LOGIN_TWO_FACTOR_AUTH_BUTTON_ID, engagementMs, {
		form_id: LOGIN_FORM_ID,
		location: trackingLocation.value,
		attempt: 'cancelled'
	})
}

const handle2FAClose = () => {
	const engagementMs = stopTimer(TWO_FA_TIMER_KEY)

	// Only track if we actually had engagement time (timer was running)
	if (engagementMs > 0) {
		trackEngagement(LOGIN_TWO_FACTOR_AUTH_ENGAGEMENT_ACTION_ID, LOGIN_TWO_FACTOR_AUTH_BUTTON_ID, engagementMs, {
			form_id: LOGIN_FORM_ID,
			location: trackingLocation.value,
			attempt: 'modal closed'
		})
	}
}
</script>
